import moment from "moment";
import { useTranslation } from "react-i18next";
import { Col, Form, Row } from "react-bootstrap";
import { getUserPayload, unloadWidget } from "../../../../system";
import { useDispatch, useSelector } from "react-redux";
import { allSharedComponents } from "../../../../shared";
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import {
  competitionActions,
  authActions,
  paymentActions,
  SubscriptionActions,
} from "../../../../redux/actions";
import {
  translationConstants,
  sportConstants,
  competitionConstants,
  Directions,
  paymentType,
  TabsConstants,
  timezoneConstants,
} from "../../../../constants";
import UpcomingCompetitions from "../Competitions/Tabs/UpcomingCompetitions";
import PreviousCompetitions from "../Competitions/Tabs/PreviousCompetitions";
import {
  ArrowLeftFilledIcon,
  ArrowRightFilledIcon,
} from "../../../../assets/images/icon";
import {
  dateToUTCWithTimezone,
  getEpochMonthStartAndEndDate,
  getUserBoatClass,
} from "../../../../system/";

const getInitialGridState = () => {
  const currentDate = dateToUTCWithTimezone();
  const { epochMonthWithFirstDate, epochMonthWithLastDate } =
    getEpochMonthStartAndEndDate(currentDate);

  return {
    length: 10,
    pageNumber: 1,
    orderBy: "startDate asc",
    filterQuery: `status != 3 && (registrationStartDate <= ${epochMonthWithLastDate} && endDate >= ${epochMonthWithFirstDate})`,
    search: null,
  };
};

const {
  CustomModal,
  BaseForm,
  RegistrationPaymentModal,
  TimezoneDisplayText,
  BoatClassPaymentModal,
} = allSharedComponents;

const PublicCompetitions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [urlCheckoutId, setUrlCheckoutId] = useState(
    new URLSearchParams(location?.search)?.get("id")
  );

  const userPayload = getUserPayload();
  const [isShow, setShow] = useState(false);
  const [key, setKey] = useState(TabsConstants.NUMERIC_UPCOMING);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [gridState, setGridState] = useState(getInitialGridState());
  const [dateFilter, setDateFilter] = useState(moment());

  const [competitionFilter, setCompetitionFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");

  const isParticipantsValidated = useSelector(
    (state) => state.CompetitionReducer?.isParticipantsValidated
  );
  const publicCompetitions = useSelector(
    (state) => state.CompetitionReducer.competitionPublicData
  );
  const isAuthenticated = useSelector(
    (state) => state.LoginReducer.isAuthenticated
  );
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );
  const CompetitionData = useSelector((state) => {
    return state.CompetitionReducer.competitionPublicDataById;
  });

  const updateFilterQuery = useCallback(() => {
    const filterConditions = [];
    const { epochMonthWithFirstDate, epochMonthWithLastDate } =
      getEpochMonthStartAndEndDate(dateFilter);
    let orderByConditions;
    if (key === TabsConstants.NUMERIC_UPCOMING) {
      filterConditions.push("status != 3");
      orderByConditions = "startDate asc";
    } else {
      filterConditions.push("status == 3");
      orderByConditions = "startDate desc";
    }
    if (sportFilter !== "") {
      filterConditions.push(`sportId == ${sportFilter}`);
    }

    if (competitionFilter !== "") {
      filterConditions.push(
        `isPublicRegistrationAllowed == ${
          competitionFilter === competitionConstants.PUBLIC ? "true" : "false"
        }`
      );
    }

    if (dateFilter) {
      filterConditions.push(
        `(registrationStartDate <= ${epochMonthWithLastDate} && endDate >= ${epochMonthWithFirstDate})`
      );
    }

    const filterQuery = filterConditions.join(" && ");
    const orderBy = orderByConditions;
    setGridState((prevGridState) => ({
      ...prevGridState,
      filterQuery,
      orderBy,
    }));
  }, [sportFilter, competitionFilter, dateFilter, key]);

  useEffect(() => {
    if (isParticipantsValidated) {
      onSetShow();
    }
    // eslint-disable-next-line
  }, [isParticipantsValidated]);

  useEffect(() => {
    updateFilterQuery();
    //eslint-disable-next-line
  }, [updateFilterQuery]);

  useEffect(() => {
    dispatch(competitionActions.getPublicCompetitions(gridState));
    //eslint-disable-next-line
  }, [gridState]);

  const onSetShow = () => {
    setShow((prev) => !prev);
    if (isShow) {
      resetAllStates();
    }
    if (!isAuthenticated) {
      dispatch(authActions.setShowLoginModal(true));
      dispatch(authActions.setLoginFromRegistration(true));
      setSelectedCompetition(null);
    }
  };

  const resetAllStates = () => {
    unloadWidget();
    setSelectedCompetition(null);
    dispatch(competitionActions.validateParticipentsForCompetitionFalse());
    dispatch(paymentActions.paymentCheckoutNull());
  };

  useEffect(() => {
    if (selectedCompetition) {
      if (isAuthenticated) {
        dispatch(
          competitionActions.getPublicCompetitionById(
            selectedCompetition,
            () => {
              dispatch(
                competitionActions.validateParticipentsForCompetition(
                  userPayload?.user?.id,
                  selectedCompetition,
                  setSelectedCompetition
                )
              );
            }
          )
        );
      } else {
        onSetShow();
      }
    }
    //eslint-disable-next-line
  }, [selectedCompetition, isAuthenticated]);

  useEffect(() => {
    if (urlCheckoutId) {
      navigate(location.pathname);
      setUrlCheckoutId(null);
      const boatClassId = getUserBoatClass();
      dispatch(
        competitionActions.addParticipantWithPayment(
          urlCheckoutId,
          boatClassId,
          () => {
            dispatch(paymentActions.paymentCheckoutNull());
            dispatch(competitionActions.getPublicCompetitions(gridState));
          }
        )
      );
    }
    // eslint-disable-next-line
  }, [urlCheckoutId]);

  const handleClearFilters = () => {
    setSportFilter("");
    setCompetitionFilter("");
    setDateFilter(dateToUTCWithTimezone());
  };

  const nextYear = () => {
    setDateFilter((prevDate) =>
      dateToUTCWithTimezone(prevDate).add(1, "month")
    );
  };

  const prevYear = () => {
    setDateFilter((prevDate) =>
      dateToUTCWithTimezone(prevDate).subtract(1, "month")
    );
  };

  useEffect(() => {
    dispatch(authActions.fetchCities(process.env.REACT_APP_Country_Id));
    dispatch(
      SubscriptionActions.GetAllSubscriptionPrices({ isLoading: false })
    );
    // eslint-disable-next-line
  }, []);

  return (
    <div className="container UserDiv pe-4 py-2">
      <Row className="public-competition d-flex flex-column flex-md-row">
        <Col md={4}>
          <p className="UserTabHeading m-0">
            {t(translationConstants.COMPETITIONS)}
          </p>
        </Col>
        <Col
          sm={12}
          lg={8}
          className="d-flex flex-column flex-lg-row gap-2 justify-content-lg-end justify-content-center align-items-center px-3"
        >
          <div className="py-3 d-flex mx-2">
            {direction === Directions.rtl ? (
              <img
                src={ArrowRightFilledIcon}
                alt="center-logo"
                onClick={nextYear}
              />
            ) : (
              <img
                src={ArrowLeftFilledIcon}
                alt="center-logo"
                onClick={prevYear}
              />
            )}
            <span className="px-2">
              {dateFilter.format(timezoneConstants.monthYearFullWordFormat)}
            </span>
            {direction === Directions.rtl ? (
              <img
                src={ArrowLeftFilledIcon}
                alt="center-logo"
                onClick={prevYear}
              />
            ) : (
              <img
                src={ArrowRightFilledIcon}
                alt="center-logo"
                onClick={nextYear}
              />
            )}
          </div>
          <div className="col-lg-3 col-md-12 col-12 d-flex gap-2 justify-content-center justify-content-md-end align-items-center">
            <Form.Select
              className="custom-select-icon filter-style px-2"
              aria-label="Event filter select"
              onChange={(event) => setCompetitionFilter(event.target.value)}
              value={competitionFilter}
            >
              <option value="">
                {t(translationConstants.SHOW_ALL_COMPETITIONS)}
              </option>
              <option value={competitionConstants.PUBLIC}>
                {t(translationConstants.PUBLIC_COMPETITIONS)}
              </option>
              <option value={competitionConstants.PRIVATE}>
                {t(translationConstants.PRIVATE_COMPETITIONS)}
              </option>
            </Form.Select>
          </div>
          <div className="col-lg-3 col-12 d-flex justify-content-center justify-content-md-end align-items-center">
            <Form.Select
              id="sportId"
              className="custom-select-icon filter-style px-2 form-select"
              value={sportFilter}
              onChange={(event) => setSportFilter(event.target.value)}
            >
              <option value="">
                {t(translationConstants.SELECT_COMPETITION_SPORT_TYPE)}
              </option>
              {Object.entries(sportConstants).map(([sport, value]) => (
                <option key={value} value={value}>
                  {t(translationConstants[sport])}
                </option>
              ))}
            </Form.Select>
          </div>
          {(sportFilter ||
            competitionFilter ||
            !dateFilter.isSame(dateToUTCWithTimezone(), "month")) && (
            <div className="col-lg-2 col-md-12 col-12 d-flex gap-2 justify-content-center justify-content-lg-end align-items-center w-auto">
              <div className="cancel-filter" onClick={handleClearFilters}>
                {t(translationConstants.CLEAR_FILTERS)}
              </div>
            </div>
          )}
        </Col>
      </Row>
      <div>
        <div className="view-center-profile-scroll-tabs">
          <Tabs
            id="controlled-tab-example"
            selectedIndex={key}
            onSelect={(k) => setKey(k)}
          >
            <div className="row d-flex align-items-center">
              <div className="col-md-8 col-12 ">
                <TabList>
                  <Tab eventKey={TabsConstants.NUMERIC_UPCOMING}>
                    {t(translationConstants.UPCOMING)}
                  </Tab>
                  <Tab eventKey={TabsConstants.NUMERIC_PREVIOUS}>
                    {t(translationConstants.PREVIOUS)}
                  </Tab>
                </TabList>
              </div>
            </div>
            <TabPanel>
              <TimezoneDisplayText />
              <UpcomingCompetitions
                flag={
                  key === TabsConstants.NUMERIC_UPCOMING
                    ? competitionConstants.UPCOMING
                    : competitionConstants.PREVIOUS
                }
                setGridState={setGridState}
                gridState={gridState}
                publicCompetitions={publicCompetitions}
                setSelectedCompetition={setSelectedCompetition}
              />
            </TabPanel>
            <TabPanel>
              <TimezoneDisplayText />
              <PreviousCompetitions
                flag={
                  key === TabsConstants.NUMERIC_UPCOMING
                    ? competitionConstants.UPCOMING
                    : competitionConstants.PREVIOUS
                }
                setGridState={setGridState}
                gridState={gridState}
                publicCompetitions={publicCompetitions}
              />
            </TabPanel>
          </Tabs>
        </div>
      </div>

      {isAuthenticated && (
        <CustomModal
          size="sm"
          show={isShow}
          scrollable={true}
          setShow={onSetShow}
        >
          {CompetitionData?.registrationFee ? (
            <BaseForm customClass="payment-modal-min-height">
              <RegistrationPaymentModal
                title={t(translationConstants.REGISTER_FOR_COMPETITION)}
                eventID={selectedCompetition}
                type={paymentType.Competition}
              />
            </BaseForm>
          ) : (
            <BaseForm customClass="free-competition-payment-modal">
              <BoatClassPaymentModal
                setShow={setShow}
                resetAllStates={resetAllStates}
                selectedCompetition={selectedCompetition}
                onSuccess={() => {
                  resetAllStates();
                  dispatch(competitionActions.getPublicCompetitions(gridState));
                }}
              />
            </BaseForm>
          )}
        </CustomModal>
      )}
    </div>
  );
};

export default PublicCompetitions;
